import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import Users from "@/pages/Users";
import Templates from "@/pages/Templates";
import Settings from "@/pages/Settings";
import Integrations from "@/pages/Integrations";
import ServiceCRM from "@/pages/ServiceCRM";
import Broadcast from "@/pages/Broadcast";
import ServiceDetail from "@/pages/ServiceDetail";
import ProductMaster from "@/pages/ProductMaster";
import ServiceTypeMaster from "@/pages/ServiceTypeMaster";
import InstallPrompt from "@/components/InstallPrompt";
import ErrorBoundary from "@/components/ErrorBoundary";

const STAFF_ROLES = ["admin", "manager"];
const ALL_ROLES = ["admin", "manager", "salesperson"];

function Protected({ children, roles, allowedDepts }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-[#6B7280]">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;

  if (user.role === "salesperson" && allowedDepts) {
    const dept = (user.department || "all").toLowerCase();
    const hasAccess = dept === "all" || dept === "both" || allowedDepts.includes(dept);
    if (!hasAccess) {
      if (dept === "service") return <Navigate to="/service-crm" replace />;
      if (dept === "lead") return <Navigate to="/leads" replace />;
      return <Navigate to="/" replace />;
    }
  }

  return <Layout><ErrorBoundary>{children}</ErrorBoundary></Layout>;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <InstallPrompt />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Protected roles={ALL_ROLES}><Dashboard /></Protected>} />
          <Route path="/leads" element={<Protected roles={ALL_ROLES} allowedDepts={["lead"]}><Leads /></Protected>} />
          <Route path="/leads/:id" element={<Protected roles={ALL_ROLES} allowedDepts={["lead"]}><LeadDetail /></Protected>} />
          <Route path="/service-crm" element={<Protected roles={ALL_ROLES} allowedDepts={["service"]}><ServiceCRM /></Protected>} />
          <Route path="/service-crm/:id" element={<Protected roles={ALL_ROLES} allowedDepts={["service"]}><ServiceDetail /></Protected>} />
          
          {/* Master routes - Admin only */}
          <Route path="/templates" element={<Protected roles={["admin"]}><Templates /></Protected>} />
          <Route path="/products" element={<Protected roles={["admin"]}><ProductMaster /></Protected>} />
          <Route path="/masters/products" element={<Protected roles={["admin"]}><ProductMaster /></Protected>} />
          
          {/* Staff routes - Admin & Manager */}
          <Route path="/users" element={<Protected roles={STAFF_ROLES}><Users /></Protected>} />
          <Route path="/integrations" element={<Protected roles={STAFF_ROLES}><Integrations /></Protected>} />
          <Route path="/broadcast" element={<Protected roles={STAFF_ROLES}><Broadcast /></Protected>} />
          <Route path="/settings" element={<Protected roles={STAFF_ROLES}><Settings /></Protected>} />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
